import { useQuery } from '@tanstack/react-query';
import queryKeys from '../reactQuery/queryKeys';
import getHotelById from '../hotel/getHotelById';
import getBooking from '../cancellation/getBooking';
import { Hotel } from '../isValidHotel';
import { authService } from '../account/AuthService';

export type GetBookingResult = Omit<Awaited<ReturnType<typeof getBooking>>, 'hotel'> & {
  hotel: Hotel;
};

export type Params = {
  bookingId: string;
  pinCode?: string;
  authToken?: string;
  locale: string;
  accountId?: string;
  userInitialized?: boolean;
  routerIsReady?: boolean;
};

export const fetchGetBooking = async (
  { bookingId, pinCode, authToken, locale }: Params,
  signal?: AbortSignal
): Promise<GetBookingResult> => {
  // eslint-disable-next-line no-console
  const userSession = await authService.getSession().catch((err) => console.log(err));
  let accountId;
  let idToken;
  if (!pinCode && !authToken && userSession) {
    accountId = await authService.getAccount(userSession);
    idToken = userSession?.tokens?.accessToken?.toString();
  }
  const result = await getBooking(
    {
      bookingId,
      pinCode,
      authToken,
      accountId,
      idToken,
      locale,
    },
    signal
  );

  const hotel = await getHotelById(
    {
      hotelId: result?.hotel?.propertyId as string,
      locale: locale as string,
    },
    signal
  );

  return {
    ...result,
    hotel: {
      ...hotel,
      status: result.hotel?.status,
    },
  };
};

const useGetBooking = ({
  bookingId,
  pinCode,
  authToken,
  locale,
  userInitialized,
  accountId,
  routerIsReady,
}: Params) => {
  const response = useQuery<GetBookingResult, Error>({
    queryKey: queryKeys.booking({ bookingId, locale, pinCode, authToken }),
    queryFn: ({ signal }) => fetchGetBooking({ bookingId, pinCode, authToken, locale }, signal),
    keepPreviousData: true,
    staleTime: 5000 * 60,
    enabled:
      !!bookingId &&
      !!locale &&
      userInitialized &&
      routerIsReady &&
      (!!pinCode || !!authToken || !!accountId),
  });
  return response;
};

export default useGetBooking;
